// src/pages/PlayCartellaPage.jsx - COMPLETE FIXED VERSION

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useContext,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../axiosInstance.js";

import { LanguageContext } from "../contexts/LanguageContext.js";
import PageWrapper from "../components/PageWrapper.jsx";
import SharedHeader from "../common/SharedHeader.jsx";
import Sidebar from "../components/Sidebar.jsx";
import FullBingoBoard from "../components/FullBingoBoard.jsx";
import MiniBingoBoard from "../components/MiniBingoBoard.jsx";
import { ToastContainer, toast } from "react-toastify";

import BingoBall, { getBallColor } from "../components/BingoBall.jsx";

import useAudioPlayer from "../hooks/useAudioPlayer.js";
import useGameSocket from "../hooks/useGameSocket.js";

const BALL_CALL_INTERVAL = 4000;

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

const translations = {
  en: {
    loadingGame: "Loading game...",
    gameIdMissing: "Game ID is missing. Redirecting to stake selection.",
    noCartella:
      "No cartella assigned for this game or user. Please select one.",
    fetchCartellaError:
      "Failed to retrieve your cartella. Please ensure you have selected one.",
    cartellaIdError: "Could not determine your cartella ID. Redirecting.",
    authError: "User is not authenticated. Redirecting to login.",
    fetchBoardError: "Failed to fetch cartella board details.",
    fetchGameError: "Failed to fetch initial game state.",
    loadGameError: "Failed to load game data.",
    socketAuthError:
      "Authentication token missing for socket. Redirecting to login...",
    socketDisconnected:
      "Socket Disconnected: {reason}. Attempting to reconnect...",
    socketConnectError:
      "Socket connection failed: {error}. Please check your network.",
    socketError: "Socket Error: {message}",
    winMessage: "You won {amount} Birr!",
    winSharedMessage: "You won {amount} Birr! (Shared with {count} others)",
    winnerMessage: "Winner: {name} won {amount} Birr!",
    gameEndedMessage: "The game has ended. {count} players shared the win.",
    noBingoMessage: "All numbers have been called. Game ended without a Bingo!",
    gameCancelled:
      "Game cancelled due to insufficient players. Stakes refunded.",
    congratulations: "Congratulations! Your winnings will be credited shortly.",
    gameOver: "GAME OVER",
    betterLuck: "Better luck next time!",
    playAgain: "Play Again!",
    winPotential: "Win",
    stake: "Stake",
    called: "Call",
    player: "Player",
    currentCall: "CURRENT CALL",
    previousCalls: "PREVIOUS CALLS",
    noPreviousCalls: "No previous calls yet",
    cardNumber: "Card No. {number}",
    bingoBoard: "75 Ball Bingo Board",
    bingo: "Bingo!",
    isComing: "is coming your way!",
    won: "Won:",
    multipleWinners: "Multiple Winners:",
    phone: "Phone:",
    gameStatus: "Game Status:",
    gameStarts: "Game Starts In:",
    waitingPlayers: "Waiting for game to become active or more players...",
    goBack: "Go Back to Stakes",
    youSharedTheWin: "YOU SHARED THE WIN!",
    youWonText: "YOU WON!",
    waitingForConnection: "Waiting for connection...",
    playing: "Playing...",
    loadingBoard: "Loading Bingo Board...",
    waitingBallCall: "Waiting...",
    unknownPlayer: "Unknown Player",
    birr: "Birr",
    sharedWithOthers: "(Shared with {count} others)",
    winner: "Winner",
    winners: "Winners",
    gameEnded: "The game has ended",
    aPlayer: "A player",
    winningCartellaTitle: "Winning Cartella",
    winningCells: "Winning Cells",
    mute: "Mute",
    unmute: "Unmute",
    free: "FREE",
    autoMarking: "Auto-marking numbers...",
    markingSaved: "Markings saved",
    markingFailed: "Failed to save markings",
    cannotMarkUncalled: "You can only mark called numbers.",
    autoMode: "Auto",
    manualMode: "Manual",
    insufficientBalance:
      "Insufficient balance! You need ETB {amount} to select this cartella.",
  },
  am: {
    loadingGame: "ጨዋታ እየጫነ ነው...",
    gameIdMissing: "የጨዋታ መታወቂያ ጠፍቷል። ወደ ውርርድ ምርጫ በመሄድ ላይ...",
    noCartella: "ለዚህ ጨዋታ ወይም ተጠቃሚ ካርቴላ አልተመደበም። እባክዎ አንዱን ይምረጡ።",
    fetchCartellaError: "ካርቴላዎን ማግኘት አልተቻለም። አንዱን መርጠው መሆኑን ያረጋግጡ።",
    cartellaIdError: "የካርቴላ መታወቂያ ሊወሰን አልቻለም። በመሄድ ላይ...",
    authError: "ተጠቃሚው አልተረጋገጠም። ወደ መግቢያ በመሄድ ላይ...",
    fetchBoardError: "የካርቴላ ሰሌዳ ዝርዝሮችን ማግኘት አልተቻለም።",
    fetchGameError: "የጨዋታ መነሻ ሁኔታ ማግኘት አልተቻለም።",
    loadGameError: "የጨዋታ ውሂብ ማግኘት አልተቻለም።",
    socketAuthError: "ለሶኬት የማረጋገጫ ቶከን ጠፍቷል። ወደ መግቢያ በመሄድ ላይ...",
    socketDisconnected: "ግንኙነት ተቋርጧል: {reason}. እንደገና በማገናኘት ላይ...",
    socketConnectError: "ግንኙነት አልተሳካም: {error}. እባክዎ ኔትወርክዎን ያረጋግጡ።",
    socketError: "የግንኙነት ስህተት: {message}",
    winMessage: "አሸንፈዋል {amount} ብር!",
    winSharedMessage: "አሸንፈዋል {amount} ብር! (ከ{count} ሌሎች ጋር ተካፍለዋል)",
    winnerMessage: "አሸናፊ: {name} በ{amount} ብር አሸንፏል!",
    gameEndedMessage: "ጨዋታው አልቋል። {count} ተጫዋቾች ድል አጋርተዋል።",
    noBingoMessage: "ሁሉም ቁጥሮች ተጠርተዋል። ጨዋታው ያለ ቢንጎ አልቋል!",
    gameCancelled: "ጨዋታው በቂ ተጫዋቾች ስለሌሉ ተሰርዟል። ውርይቶች ተመልሰዋል።",
    congratulations: "እንኳን ደስ አለዎት! ማሸነፍዎ በቅርቡ ይከፈላል።",
    gameOver: "ጨዋታው አልቋል",
    betterLuck: "በሚቀጥለው ጊዜ ይስማማሎት!",
    playAgain: "እንደገና ይጫወቱ!",
    winPotential: "ድል",
    stake: "ውርርድ",
    called: "ተጠርቷል",
    player: "ተጫዋች",
    currentCall: "የተጠራ",
    previousCalls: "ቀደም ብለው የተጠሩ",
    noPreviousCalls: "ምንም ቀደም ብሎ የተጠረ የለም",
    cardNumber: "የካርታ ቁጥር {number}",
    bingoBoard: "75 ኳስ ቢንጎ ሰሌዳ",
    bingo: "ቢንጎ!",
    isComing: "ወደ እርስዎ ይመጣል!",
    won: "የማሸነፍ መጠን:",
    multipleWinners: "ብዙ አሸናፊዎች:",
    phone: "ስልክ:",
    gameStatus: "የጨዋታ ሁኔታ:",
    gameStarts: "ጨዋታ ይጀምራል:",
    waitingPlayers: "ጨዋታ እንዲሰራ ወይም ተጫዋቾች እየጠበቁ ነው...",
    goBack: "ወደ አሸናፊዎች ተመለስ",
    youSharedTheWin: "ድል አጋራችሁ!",
    youWonText: "አሸንፈዋል!",
    waitingForConnection: "ግንኙነት በመጠበቅ ላይ...",
    playing: "ድምፅ እየተጫወተ ነው...",
    loadingBoard: "ቢንጎ ሰሌዳ እየጫነ ነው...",
    waitingBallCall: "በመጠበቅ ላይ...",
    unknownPlayer: "ያልታወቀ ተጫዋች",
    birr: "ብር",
    sharedWithOthers: "(ከ{count} ሌሎች ጋር ተካፍለዋል)",
    winner: "አሸናፊ",
    winners: "አሸናፊዎች",
    gameEnded: "ጨዋታው አልቋል",
    aPlayer: "አንድ ተጫዋች",
    winningCartellaTitle: "የአሸናፊው ካርቴላ",
    winningCells: "አሸናፊ ቁጥሮች",
    mute: "ድምፅ አጥፋ",
    unmute: "ድምፅ ክፈት",
    free: "ነፃ",
    autoMarking: "ቁጥሮች በራስ ምልክት እየተደረጉ ነው...",
    markingSaved: "ምልክቶች ተቀምጠዋል",
    markingFailed: "ምልክቶች ማስቀመጥ አልተቻለም",
    cannotMarkUncalled: "የተጠሩ ቁጥሮችን ብቻ ምልክት ማድረግ ይችላሉ።",
    autoMode: "አውቶ",
    manualMode: "እጅ",
    insufficientBalance: "በቂ ሂሳብ የለም! ይህን ካርቴላ ለመምረጥ ETB {amount} ያስፈልግዎታል።",
  },
  or: {
    loadingGame: "Taphannoo taphaa...",
    gameIdMissing:
      "ID taphaa hin argamne. Filannoo kaffaltii irratti deebi'uu.",
    noCartella:
      "Cartella tapha kana yookiin fayyadamaa irratti hin murteeffanne. Maaloo filadhu.",
    fetchCartellaError:
      "Cartella keessan argachuu hindandeenye. Maaloo filatamuu keessan mirkaneessaa.",
    cartellaIdError: "ID cartella keessan hin murteeffanne. Deebi'uu.",
    authError: "Fayyadamaa hin mirkaneessine. Seensa irratti deebi'uu.",
    fetchBoardError: "Garaagarummaa boradi cartellaa argachuu hindandeenye.",
    fetchGameError: "Haala jalqabaa taphaa argachuu hindandeenye.",
    loadGameError: "Odeeffannoo taphaa argachuu hindandeenye.",
    socketAuthError:
      "Token mirkaneessaa soketii hin argamne. Seensa irratti deebi'uu...",
    socketDisconnected:
      "Soketiin kan hin walqabne: {reason}. Irra deebi'uu irratti...",
    socketConnectError:
      "Walqunnamtii soketii hin taane: {error}. Maaloo network keessan mirkaneessaa.",
    socketError: "Dogoggora walqunnamtii: {message}",
    winMessage: "{amount} Birr moo'atte!",
    winSharedMessage:
      "{amount} Birr moo'atte! (Waliin {count} kan biraatiin qoodame)",
    winnerMessage: "Moo'ataa: {name} {amount} Birr moo'atte!",
    gameEndedMessage:
      "Tapha xumureera. {count} taphootni moo'ata waliin qoodu.",
    noBingoMessage:
      "Lakkoofsota hundi waamamanii jiru. Tapha Bingo malee xumureera!",
    gameCancelled:
      "Tapha taphoota ga'umsaan hin guutneef dhaabbate. Kaffaltiidhaan deebifame.",
    congratulations: "Baga gammaddan! Moo'ata keessan dhiyoo dhiyaata.",
    gameOver: "TAPHA XUMUREERA",
    betterLuck: "Carraa itti aanu carraa gaarii haa ta'u!",
    playAgain: "Irra deebi'i Taphadhu!",
    winPotential: "Moo'ata",
    stake: "Kaffaltii",
    called: "Waamame",
    player: "Taphataa",
    currentCall: "WAAMAMA AMAA",
    previousCalls: "WAAMAMOLEE DURAA",
    noPreviousCalls: "Waamolee duriin hin jiru",
    cardNumber: "Lakkoofsa Kaardii {number}",
    bingoBoard: "Booradi Bingo 75 Bala'aa",
    bingo: "Bingo!",
    isComing: "keessan irratti dhufuu!",
    won: "Moo'ate:",
    multipleWinners: "Moo'atoota hedduun:",
    phone: "Bilbila:",
    gameStatus: "Haala Taphaa:",
    gameStarts: "Tapha jalqaba:",
    waitingPlayers: "Tapha sochuu yookiin taphoota dabalataaf eegaa...",
    goBack: "Filannoo Kaffaltii Irratti Deebi'i",
    youSharedTheWin: "MOO'ATA WALII GALAA KEESSAN!",
    youWonText: "MOO'ATTAN!",
    waitingForConnection: "Walqunnamtii eegaa...",
    playing: "Taphannaa jira...",
    loadingBoard: "Booradi Bingo fe'amaa jira...",
    waitingBallCall: "Eegaa...",
    unknownPlayer: "Beettibeyna taphataa",
    birr: "Birr",
    sharedWithOthers: "(Moo'ata {count} haraataara shaaretta)",
    winner: "Moo'ata",
    winners: "Moo'atoota",
    gameEnded: "Tapha xumureera",
    aPlayer: "Hade taphataa",
    winningCartellaTitle: "Cartella Moo'ataa",
    winningCells: "Cells Moo'ataa",
    mute: "Maaloo",
    unmute: "Maaloo dhageettii godhaa",
    free: "BILISA",
    autoMarking: "Lakkoofsota mallatteessaa...",
    markingSaved: "Mallatteessi eegame",
    markingFailed: "Mallatteessi eegamuu hin dandeenye",
    cannotMarkUncalled: "Lakkoofsota waamaman qofa mallatteessuu dandeessu.",
    autoMode: "Awtomatiik",
    manualMode: "Harkaan",
    insufficientBalance:
      "Maallaqni keessan hin gahu! Kaardii kana filachuuf ETB {amount} ni barbaadu.",
  },
  ti: {
    loadingGame: "ጸዊዑ ዘሎ ጸወታ...",
    gameIdMissing: "ID ጸወታ ኣይተረኽበን። ናብ ምርጫ ውሕስነት ተመልሰ።",
    noCartella: "ናይ እዚ ጸወታ ወይ ተጻወቲ ካርቴላ ኣይተመዝገበን። በጃኹም ሓደ ምረጹ።",
    fetchCartellaError: "ካርቴላኹም ክትረኽቡ ኣይከኣልኩምን። በጃኹም ምርጫኹም ከምዝግበረሉ ኣረጋግጹ።",
    cartellaIdError: "ID ካርቴላኹም ክምዝገብ ኣይከኣለን። ተመሊሱ።",
    authError: "ተጻወቲ ኣይምረጋገጸን። ናብ ምእታው ተመልሰ።",
    fetchBoardError: "ዝርዝር ሰሌዳ ካርቴላ ክትረኽቡ ኣይከኣልኩምን።",
    fetchGameError: "መጀመርታ ኩነታት ጸወታ ክትረኽቡ ኣይከኣልኩምን።",
    loadGameError: "ውሂብ ጸወታ ክትረኽቡ ኣይከኣልኩምን።",
    socketAuthError: "ምልክት ምርግጋጽ ሶኬት ኣይተረኽበን። ናብ ምእታው ተመልሰ።...",
    socketDisconnected: "ምትእስሳር ተቋሪጹ: {reason}. ዳግማይ ክትተሓሓዝ ኣሎኩም...",
    socketConnectError: "ምትእስሳር ኣይተኻእለን: {error}. እባክዎ ኔትወርክኩም ኣረጋግጹ።",
    socketError: "ጌጋ ምትእስስር: {message}",
    winMessage: "{amount} ብር ተዓዊትኩም!",
    winSharedMessage: "{amount} ብር ተዓዊትኩም! (ምስ {count} ካልኦት ተካፊለኹም)",
    winnerMessage: "ዓወተኛ: {name} {amount} ብር ተዓዊቱ!",
    gameEndedMessage: "ጸወታ ተዛዚማ። {count} ተጻወትቲ ዓወት ተካፊሎም።",
    noBingoMessage: "ኩሎም ቁጽርታት ተዋሂቦም ኣለዉ። ጸወታ ቢንጎ ዘይብላ ተዛዚማ!",
    gameCancelled: "ጸወታ ብሰንኪ ብቡዙሕ ተጻወትቲ ዘይምህላውና ተቋሪጻ። ውሕስነት ተመሊሱ።",
    congratulations: "እንኳን ደስ አለዎት! ማሸነፍዎ በቅርቡ ይከፈላል።",
    gameOver: "ጸወታ ተዛዚማ",
    betterLuck: "ኣብ ዝመጽእ ግዜ ዕድል ይህሉዎ!",
    playAgain: "ዳግማይ ተጻወቱ!",
    winPotential: "ዓወት",
    stake: "ውሕስነት",
    called: "ተዋሂቡ",
    player: "ተጻወቲ",
    currentCall: "ናይ ሕጂ ድሕረ ባይታ",
    previousCalls: "ቅድሚ ሕጂ ዝተዋህበ",
    noPreviousCalls: "ዝኾነ ቅድሚ ሕጂ ዝተዋህበ የለን",
    cardNumber: "ቁጽሪ ካርድ {number}",
    bingoBoard: "75 ኳስ ቢንጎ ሰሌዳ",
    bingo: "ቢንጎ!",
    isComing: "ናብ ኣቦኻትኩም ይመጽእ!",
    won: "ተዓዊቱ:",
    multipleWinners: "ዝበዝሑ ዓወተኛታት:",
    phone: "ተሌፎን:",
    gameStatus: "ኩነታት ጸወታ:",
    gameStarts: "ጸወታ ክትጀምር:",
    waitingPlayers: "ጸወታ ክትካየድ ወይ ተጻወትቲ ክውሰኹ ተጸበዩ...",
    goBack: "ናብ ምርጫ ውሕስነት ተመልሰ",
    youSharedTheWin: "ዓወት ተካፊልኩም!",
    youWonText: "ተዓዊትኩም!",
    waitingForConnection: "ምትእስሳር ይጽበ...",
    playing: "ይጻወት ኣሎ...",
    loadingBoard: "ቢንጎ ቦርድ ይጽበ...",
    waitingBallCall: "ይጽበ...",
    unknownPlayer: "ዘይፍለጥ ተጻዋታይ",
    birr: "ብር",
    sharedWithOthers: "(ምስ {count} ካልኦት ተካፊለኹም)",
    winner: "ዓወተኛ",
    winners: "ዓወተኛታት",
    gameEnded: "ጸወታ ተዛዚማ",
    aPlayer: "ሓደ ተጻዋታይ",
    winningCartellaTitle: "ካርቴላ ዓወተኛ",
    winningCells: "ዓወተኛ ሕዋሳት",
    mute: "ድምጺ ኣጥፍእ",
    unmute: "ድምጺ ክፈት",
    free: "ናጻ",
    autoMarking: "ቁጽርታት ምልክት ይግበር ኣሎ...",
    markingSaved: "ምልክታት ተቀሚጡ",
    markingFailed: "ምልክታት ክቀመጥ ኣይከኣለን",
    cannotMarkUncalled: "ዝተጸውዑ ቁጽርታት ጥራይ ምልክት ክትገብር ትኽእል ኢኻ።",
    autoMode: "ኣውቶ",
    manualMode: "ኢድ",
    insufficientBalance: "በቂ ገንዘብ የለን! ነዛ ካርቴላ ንምምራጽ ETB {amount} የድልየካ።",
  },
  wo: {
    loadingGame: "Taphay qaxarettishin...",
    gameIdMissing: "Taphay ID xayi. Doonaa dooranaara zaarettishin.",
    noCartella: "Ha taphaay woy kaysoora Cartellaa dooranabay xayi. Doorana.",
    fetchCartellaError: "Ne Cartellaa demmanawu dandayettibeyna. Doorana.",
    cartellaIdError: "Cartellaa ID qonccesettibeyna. Zaarettishin.",
    authError: "Kaysooy qonccesettibeyna. Gelaara zaarettishin.",
    fetchBoardError: "Garaagarummaa boradi cartellaa argachuu hindandeenye.",
    fetchGameError: "Haala jalqabaa taphaa argachuu hindandeenye.",
    loadGameError: "Odeeffannoo taphaa argachuu hindandeenye.",
    socketAuthError: "Socketi qonccesettibeyna. Gelaara zaarettishin...",
    socketDisconnected: "Socketi qeeri: {reason}. Haraa qeettishin...",
    socketConnectError:
      "Socketi qeettanaawu dandayettibeyna: {error}. Interneetaa xeella.",
    socketError: "Socketi dogoggora: {message}",
    winMessage: "{amount} Birr moo'atta!",
    winSharedMessage: "{amount} Birr moo'atta! ({count} haraataara shaaretta)",
    winnerMessage: "Moo'ata: {name} {amount} Birr moo'atta!",
    gameEndedMessage: "Taphay wuribeyna. {count} taphooti moo'ata shaaretta.",
    noBingoMessage: "Hundi qooraa waametta. Taphay Bingo xayibeyna!",
    gameCancelled: "Taphay taphoota xayibeyna. Kaffaletta.",
    congratulations: "Baga gammaddan! Moo'ata keessan dhiyoo dhiyaata.",
    gameOver: "TAPHA WURIBEYNA",
    betterLuck: "Haraa wode carraa gaarii haa ta'u!",
    playAgain: "Haraa tapha!",
    winPotential: "Moo'ata",
    stake: "Kaffaltii",
    called: "Waametta",
    player: "Taphataa",
    currentCall: "Ha'i waametta",
    previousCalls: "Kasee waametta",
    noPreviousCalls: "Kasee waametta xayibeyna",
    cardNumber: "Kaardaa qooraa {number}",
    bingoBoard: "75 Bolaa Bingo Boradaa",
    bingo: "Bingo!",
    isComing: "Ne doogaara yishin!",
    won: "Moo'atta:",
    multipleWinners: "Dabali moo'ata:",
    phone: "Bilbila:",
    gameStatus: "Taphay haala:",
    gameStarts: "Taphay doomana:",
    waitingPlayers: "Taphay doomana woy taphootaara eegaa...",
    goBack: "Doonaa dooranaara zaara",
    youSharedTheWin: "MOO'ATA SHAARITTA!",
    youWonText: "MOO'ATTA!",
    waitingForConnection: "Qeettanaawu eegaa...",
    playing: "Taphishin...",
    loadingBoard: "Bingo Boradaa qaxarettishin...",
    waitingBallCall: "Eegaa...",
    unknownPlayer: "Beettibeyna taphataa",
    birr: "Birr",
    sharedWithOthers: "(Moo'ata {count} haraataara shaaretta)",
    winner: "Moo'ata",
    winners: "Moo'atoota",
    gameEnded: "Tapha wuribeyna",
    aPlayer: "Hade taphataa",
    winningCartellaTitle: "Moo'ata Cartellaa",
    winningCells: "Moo'ata Bollay",
    mute: "Dumbbaa",
    unmute: "Dumbbaa kessa",
    free: "BILISA",
    autoMarking: "Lakkoofsota mallatteessaa...",
    markingSaved: "Mallatteessi eegame",
    markingFailed: "Mallatteessi eegamuu hin dandeenye",
    cannotMarkUncalled: "Lakkoofsota waamaman qofa mallatteessuu dandeessu.",
    autoMode: "Awtomaatiki",
    manualMode: "Harkaan",
    insufficientBalance:
      "Maallaqaan gahaa miti! Cartella kana filachuuf ETB {amount} ni barbaadu.",
  },
};

// Add global error handler
if (!window._promiseRejectionHandlerAdded) {
  window.addEventListener("unhandledrejection", (event) => {
    event.preventDefault();
  });
  window._promiseRejectionHandlerAdded = true;
}

const getBallLetter = (num) => {
  if (!num) return "";
  if (num === "F") return "";
  if (num <= 15) return "B";
  if (num <= 30) return "I";
  if (num <= 45) return "N";
  if (num <= 60) return "G";
  else return "O";
};

// Helper function to reconstruct 2D board from 1D array
const reconstructBoardFrom1D = (flatBoard1D) => {
  if (!flatBoard1D || !Array.isArray(flatBoard1D)) return null;

  const board2D = {};
  const letters = ["B", "I", "N", "G", "O"];
  let flatIndex = 0;

  const columnMajorGrid = [[], [], [], [], []];

  for (let col = 0; col < 5; col++) {
    for (let row = 0; row < 5; row++) {
      if (letters[col] === "N" && row === 2) {
        columnMajorGrid[col][row] = "F";
      } else if (flatIndex < flatBoard1D.length) {
        columnMajorGrid[col][row] = flatBoard1D[flatIndex];
        flatIndex++;
      } else {
        columnMajorGrid[col][row] = null;
      }
    }
  }

  letters.forEach((letter, colIdx) => {
    const column = [];
    for (let rowIdx = 0; rowIdx < 5; rowIdx++) {
      const num = columnMajorGrid[colIdx][rowIdx];
      column.push({
        number: num,
        display: num === "F" ? "FREE" : num !== null ? num.toString() : "",
      });
    }
    board2D[letter] = column;
  });

  return board2D;
};

// BingoBoard component with proper highlighting
const BingoBoard = React.memo(
  ({
    board,
    winningCells = [],
    playerMarkedNumbers = new Set(),
    isWinningBoardDisplay = false,
    onCellClick = () => {},
    isInteractive = false,
  }) => {
    const { language } = useContext(LanguageContext);

    const t = useCallback(
      (key) => translations[language]?.[key] ?? translations.en[key],
      [language],
    );

    if (!board) return <div className="text-gray-400">{t("loadingBoard")}</div>;

    const getCellClasses = (cell) => {
      let classes =
        "aspect-square flex items-center justify-center rounded-md font-extrabold shadow-inner select-none transition-all duration-100 ease-in-out font-digital";

      classes += isWinningBoardDisplay
        ? " text-[12px] sm:text-[14px] md:text-[16px] lg:text-[18px]"
        : " text-[14px] sm:text-[16px] md:text-[18px] lg:text-[20px]";

      const cellNumber = cell.number;
      const isCellInWinningPattern = winningCells.includes(cellNumber);
      const isPlayerMarkedByCurrentUser = playerMarkedNumbers.has(cellNumber);
      const isFreeSpace = cellNumber === "F";

      if (isInteractive && !isFreeSpace) {
        classes += " cursor-pointer";
      }

      if (isFreeSpace) {
        classes += " bg-gray-600 text-white border-2 border-gray-400";
      } else if (isCellInWinningPattern && isPlayerMarkedByCurrentUser) {
        classes +=
          " bg-green-500 text-white border-2 border-green-600 ring-2 ring-green-300";
      } else if (isPlayerMarkedByCurrentUser) {
        classes +=
          " bg-yellow-400 text-purple-900 border-2 border-yellow-600 ring-2 ring-yellow-300";
      } else {
        classes += " bg-white text-gray-800 border-2 border-gray-300";
      }

      return classes;
    };

    const getLetterColor = (letter) => {
      switch (letter) {
        case "B":
          return getBallColor(1);
        case "I":
          return getBallColor(16);
        case "N":
          return getBallColor(31);
        case "G":
          return getBallColor(46);
        default:
          return getBallColor(61);
      }
    };

    const letters = ["B", "I", "N", "G", "O"];

    return (
      <div
        className="grid grid-cols-5 gap-[1px] sm:gap-[1.5px] md:gap-[2px] p-0 rounded-xl bg-white border-2 border-gray-300"
        style={{ width: "100%", maxWidth: "100vw" }}
      >
        {letters.map((letter) => (
          <div
            key={`header-${letter}`}
            className="aspect-square flex items-center justify-center text-xs sm:text-sm font-extrabold text-white rounded-md shadow-md uppercase"
            style={{ backgroundColor: getLetterColor(letter) }}
          >
            {letter}
          </div>
        ))}
        {Array.from({ length: 5 }).map((_, rowIndex) =>
          letters.map((letter) => {
            const column = board ? board[letter] : null;
            const cell = column ? column[rowIndex] : null;

            if (!cell) {
              return (
                <div
                  key={`${letter}-${rowIndex}-empty`}
                  className="aspect-square bg-gray-700 rounded-md"
                  aria-hidden="true"
                ></div>
              );
            }

            return (
              <div
                key={`${letter}-${cell.number || cell.display}-${rowIndex}`}
                className={getCellClasses(cell)}
                title={cell.display}
                onClick={() => isInteractive && onCellClick(cell.number)}
              >
                {cell.display}
              </div>
            );
          }),
        )}
      </div>
    );
  },
);

/**
 * PlayCartellaPage component
 */
const PlayCartellaPage = (props) => {
  const { language } = useContext(LanguageContext);
  const [userCartellas, setUserCartellas] = useState([]);
  const [markedNumbersByCartella, setMarkedNumbersByCartella] = useState({});
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loadingGameData, setLoadingGameData] = useState(true);
  const [winnerInfo, setWinnerInfo] = useState({
    message: "",
    noWinner: false,
    isMultiWinner: false,
    winnerId: null,
    winAmount: "0",
    winnerWinAmount: "0",
    ownerFullName: "",
    ownerPhoneNumber: null,
    winningCartellaBoard: null,
    winningPatternCells: [],
    winningCartellaAllMarkedNumbers: [],
    winningCartellaId: null,
    allWinners: [],
    allWinningBoards: [],
    titleMessage: "",
    subMessage: "",
  });
  const [playerCount, setPlayerCount] = useState(0);
  const [cardCount, setCardCount] = useState(0);
  const [gameStake, setGameStake] = useState(0);
  const [winPotential, setWinPotential] = useState(0);
  const [gameStatus, setGameStatus] = useState(null);
  const [calledBalls, setCalledBalls] = useState([]);
  const [currentBall, setCurrentBall] = useState(null);
  const [previousCalls, setPreviousCalls] = useState([]);
  const [nextDrawTime, setNextDrawTime] = useState(null);
  const [countdown, setCountdown] = useState("");
  const countdownIntervalRef = useRef(null);
  const [flipBall, setFlipBall] = useState(false);
  const [showGameEndModal, setShowGameEndModal] = useState(false);
  const [isAutoMarking, setIsAutoMarking] = useState(false);
  const [isAutoMarkEnabled, setIsAutoMarkEnabled] = useState(true);

  const isMounted = useRef(false);
  const gameEnded = useRef(false);
  const saveQueueRef = useRef(new Map());
  const savingRef = useRef(false);
  const pendingMarksRef = useRef(new Map());
  const pollingIntervalRef = useRef(null);
  const shouldStopSocketRef = useRef(false);

  const [confettiPieces, setConfettiPieces] = useState([]);
  const [fireworks, setFireworks] = useState([]);
  const animationRef = useRef(null);
  const containerRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { gameId } = location.state || {};

  const t = useCallback(
    (key, replacements = {}) => {
      let translation =
        translations[language]?.[key] || translations.en[key] || key;
      for (const [placeholder, value] of Object.entries(replacements)) {
        translation = translation.replace(`{${placeholder}}`, value);
      }
      return translation;
    },
    [language],
  );

  const showToast = useCallback((message, type = "error") => {
    toast[type](message, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  }, []);

  const {
    isMuted,
    isAudioPlaying,
    toggleMute,
    playNumberSound,
    playWinSound,
    playLoseSound,
    loadAllAudio,
    cleanupAudio,
  } = useAudioPlayer();

  const createConfetti = useCallback((count = 100) => {
    const newConfetti = [];
    for (let i = 0; i < count; i++) {
      newConfetti.push({
        id: Math.random().toString(36).substr(2, 9),
        x: Math.random() * 100,
        y: -10,
        rotation: Math.random() * 360,
        color: `hsl(${Math.random() * 360}, 100%, 50%)`,
        size: Math.random() * 10 + 5,
        speed: Math.random() * 3 + 2,
        rotationSpeed: (Math.random() - 0.5) * 10,
        shape: Math.random() > 0.5 ? "circle" : "rect",
      });
    }
    return newConfetti;
  }, []);

  const createFirework = useCallback((x, y) => {
    const particles = [];
    const color = `hsl(${Math.random() * 360}, 100%, 50%)`;
    const particleCount = 50 + Math.floor(Math.random() * 50);

    for (let i = 0; i < particleCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = Math.random() * 3 + 1;

      particles.push({
        id: Math.random().toString(36).substr(2, 9),
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        color,
        size: Math.random() * 4 + 2,
        life: 100 + Math.random() * 50,
      });
    }
    return particles;
  }, []);

  const saveMarkedNumbersForCartella = useCallback(
    async (cartellaId, markedNumbersArray) => {
      if (!gameId || gameStatus !== "active" || shouldStopSocketRef.current)
        return;

      const markedNumbersOnly = markedNumbersArray.filter((n) => n !== "F");

      try {
        const response = await axiosInstance.post(
          `/game/${gameId}/save-marked-numbers`,
          {
            cartellaId: parseInt(cartellaId),
            markedNumbers: markedNumbersOnly,
          },
        );

        if (response.data.success) {
          if (response.data.bingoClaimed) {
            console.log(`Bingo auto-claimed for cartella ${cartellaId}`);
            if (response.data.isMultiWinner) {
              showToast(
                t("winSharedMessage", {
                  amount: response.data.winAmount,
                  count: response.data.totalWinners,
                }),
                "success",
              );
            } else {
              showToast(
                t("winMessage", { amount: response.data.winAmount }),
                "success",
              );
            }
          }
          return true;
        }
        return false;
      } catch (error) {
        if (error.response?.status === 400) {
          return false;
        }
        console.error(
          `Error saving marked numbers for cartella ${cartellaId}:`,
          error,
        );
        return false;
      }
    },
    [gameId, gameStatus, t, showToast],
  );

  const processSaveQueue = useCallback(async () => {
    if (
      savingRef.current ||
      saveQueueRef.current.size === 0 ||
      shouldStopSocketRef.current
    )
      return;

    savingRef.current = true;
    try {
      const queueItems = Array.from(saveQueueRef.current.entries());
      saveQueueRef.current.clear();

      for (const [cartellaId, markedNumbers] of queueItems) {
        await saveMarkedNumbersForCartella(cartellaId, markedNumbers);
      }
    } catch (error) {
      console.error("Error processing save queue:", error);
    } finally {
      savingRef.current = false;
      if (saveQueueRef.current.size > 0 && !shouldStopSocketRef.current) {
        setTimeout(processSaveQueue, 100);
      }
    }
  }, [saveMarkedNumbersForCartella]);

  const debouncedSaveForCartella = useCallback(
    (cartellaId, markedNumbers) => {
      if (shouldStopSocketRef.current) return;
      saveQueueRef.current.set(cartellaId, markedNumbers);
      setTimeout(processSaveQueue, 500);
    },
    [processSaveQueue],
  );

  const autoMarkNumber = useCallback(
    async (calledNumber) => {
      if (
        gameStatus !== "active" ||
        !calledNumber ||
        typeof calledNumber !== "number" ||
        shouldStopSocketRef.current
      ) {
        return;
      }

      setIsAutoMarking(true);
      let hasChanges = false;
      const newMarkedState = { ...markedNumbersByCartella };

      for (const cartella of userCartellas) {
        const cartellaId = cartella.cartella_id;
        const rawBoardNumbers = cartella.numbers;

        if (rawBoardNumbers.includes(calledNumber)) {
          const currentMarked = new Set(newMarkedState[cartellaId] || ["F"]);

          if (!currentMarked.has(calledNumber)) {
            currentMarked.add(calledNumber);
            newMarkedState[cartellaId] = Array.from(currentMarked);
            hasChanges = true;
            pendingMarksRef.current.set(cartellaId, new Set(currentMarked));
          }
        }
      }

      if (hasChanges) {
        setMarkedNumbersByCartella(newMarkedState);

        for (const cartella of userCartellas) {
          const cartellaId = cartella.cartella_id;
          const markedNumbers = newMarkedState[cartellaId] || ["F"];
          debouncedSaveForCartella(cartellaId, markedNumbers);
        }
      }

      setIsAutoMarking(false);
    },
    [
      gameStatus,
      userCartellas,
      markedNumbersByCartella,
      debouncedSaveForCartella,
    ],
  );

  const handleCellClick = useCallback(
    (clickedNumber) => {
      if (gameStatus !== "active" || shouldStopSocketRef.current) return;

      if (clickedNumber !== "F" && !calledBalls.includes(clickedNumber)) {
        showToast(t("cannotMarkUncalled"), "info");
        return;
      }

      let hasChanges = false;
      const newMarkedState = { ...markedNumbersByCartella };

      for (const cartella of userCartellas) {
        const cartellaId = cartella.cartella_id;
        const rawBoardNumbers = cartella.numbers;

        const numberExistsOnCartella =
          rawBoardNumbers.includes(clickedNumber) || clickedNumber === "F";

        if (numberExistsOnCartella && clickedNumber !== "F") {
          const currentMarked = new Set(newMarkedState[cartellaId] || ["F"]);

          if (currentMarked.has(clickedNumber)) {
            currentMarked.delete(clickedNumber);
          } else {
            currentMarked.add(clickedNumber);
          }

          newMarkedState[cartellaId] = Array.from(currentMarked);
          hasChanges = true;
        }
      }

      if (hasChanges) {
        setMarkedNumbersByCartella(newMarkedState);

        for (const cartella of userCartellas) {
          const cartellaId = cartella.cartella_id;
          const markedNumbers = newMarkedState[cartellaId] || ["F"];
          debouncedSaveForCartella(cartellaId, markedNumbers);
        }
      }
    },
    [
      gameStatus,
      calledBalls,
      userCartellas,
      markedNumbersByCartella,
      debouncedSaveForCartella,
      showToast,
      t,
    ],
  );

  const loadMarkedNumbers = useCallback(async () => {
    if (!gameId) return;

    try {
      const response = await axiosInstance.get(
        `/game/${gameId}/marked-numbers`,
      );
      if (response.data.success && response.data.markedNumbersByCartella) {
        const loadedMarked = {};
        for (const [cartellaId, numbers] of Object.entries(
          response.data.markedNumbersByCartella,
        )) {
          loadedMarked[cartellaId] = Array.isArray(numbers) ? numbers : ["F"];
        }
        setMarkedNumbersByCartella(loadedMarked);
      }
    } catch (error) {
      console.error("Error loading marked numbers:", error);
    }
  }, [gameId]);

  const cleanupAndNavigate = useCallback(
    (targetPath, state) => {
      shouldStopSocketRef.current = true;
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      cleanupAudio();
      navigate(targetPath, { state });
    },
    [navigate, cleanupAudio],
  );

  const formatTime = useCallback((seconds) => {
    if (seconds === null || seconds < 0) return "00:00";
    const minutes = String(Math.floor(seconds / 60)).padStart(2, "0");
    const remainingSeconds = String(seconds % 60).padStart(2, "0");
    return `${minutes}:${remainingSeconds}`;
  }, []);

  const calculateCountdown = useCallback((drawTime) => {
    if (!drawTime) return 0;
    const now = new Date().getTime();
    const targetTime = new Date(drawTime).getTime();
    const timeLeftMs = targetTime - now;
    return Math.max(0, Math.floor(timeLeftMs / 1000));
  }, []);

  const handleGameEndLogic = useCallback(
    async (data) => {
      console.log("Game End Logic Triggered:", data);

      if (gameEnded.current) {
        console.log("Game end already processed, skipping...");
        return;
      }

      shouldStopSocketRef.current = true;
      gameEnded.current = true;
      cleanupAudio();

      setGameStatus(data.endStatus || data.status || "completed");

      const currentUserId = localStorage.getItem("userId");
      let isCurrentUserWinner = false;
      let currentUserWinAmount = "0";
      let allWinnersList = [];

      // Parse winners from data
      if (
        data.winners &&
        Array.isArray(data.winners) &&
        data.winners.length > 0
      ) {
        allWinnersList = data.winners.map((w) => ({
          userId: String(w.userId || w.user_id),
          fullName: w.fullName || w.full_name || t("unknownPlayer"),
          phoneNumber: w.phoneNumber || w.phone_number || "N/A",
          winAmount: w.winAmount || w.win_amount || "0",
          cartellaId: w.cartellaId || w.cartella_id,
          winningCells:
            w.winningPatternCells || w.winning_cells || w.winningCells || [],
        }));

        const currentUserWin = allWinnersList.find(
          (w) => w.userId === String(currentUserId),
        );

        if (currentUserWin) {
          isCurrentUserWinner = true;
          currentUserWinAmount = currentUserWin.winAmount;
        }

        console.log(
          `Found ${allWinnersList.length} winner(s):`,
          allWinnersList,
        );
        console.log(`Current user ID: ${currentUserId}`);
        console.log(`Current user is winner: ${isCurrentUserWinner}`);
      } else if (data.winnerId) {
        allWinnersList = [
          {
            userId: String(data.winnerId),
            fullName: data.ownerFullName || t("unknownPlayer"),
            phoneNumber: data.ownerPhoneNumber || "N/A",
            winAmount: data.winAmount || "0",
            cartellaId: data.winningCartellaId,
            winningCells: data.winningPatternCells || data.winning_cells || [],
          },
        ];
        isCurrentUserWinner = String(data.winnerId) === String(currentUserId);
        currentUserWinAmount = isCurrentUserWinner
          ? data.winAmount || "0"
          : "0";
      }

      // Fetch all winners' cartella boards and marked numbers
      const allWinningBoards = [];
      for (const winner of allWinnersList) {
        if (winner.cartellaId) {
          try {
            // Fetch cartella board
            const boardResponse = await axiosInstance.get(
              `/game/cartella-board-details/${winner.cartellaId}`,
            );

            // Fetch marked numbers for THIS SPECIFIC winner (not current user)
            const markedNumbersResponse = await axiosInstance.get(
              `/game/${gameId}/marked-numbers/${winner.userId}`,
            );

            let markedNumbersSet = new Set(["F"]);
            if (
              markedNumbersResponse.data.success &&
              markedNumbersResponse.data.markedNumbersByCartella
            ) {
              const markedForCartella =
                markedNumbersResponse.data.markedNumbersByCartella[
                  winner.cartellaId
                ];
              if (markedForCartella && Array.isArray(markedForCartella)) {
                markedNumbersSet = new Set(markedForCartella);
              }
            }

            // Get winning cells
            let winningCellsList = winner.winningCells || [];

            console.log(
              `Winner ${winner.fullName} - Winning cells:`,
              winningCellsList,
            );
            console.log(
              `Winner ${winner.fullName} - Marked numbers:`,
              Array.from(markedNumbersSet),
            );

            if (boardResponse.data.success && boardResponse.data.cartella) {
              allWinningBoards.push({
                userId: winner.userId,
                fullName: winner.fullName,
                cartellaId: winner.cartellaId,
                winAmount: winner.winAmount,
                boardData: boardResponse.data.cartella,
                markedNumbers: markedNumbersSet,
                winningCells: winningCellsList,
              });
            }
          } catch (err) {
            console.error(
              `Error fetching data for cartella ${winner.cartellaId}:`,
              err,
            );
          }
        }
      }

      // Prepare modal content
      let titleMessage = "";
      let modalMessage = "";
      let subMessage = "";

      if (isCurrentUserWinner) {
        if (allWinnersList.length > 1) {
          titleMessage = "🎉 YOU SHARED THE WIN! 🎉";
          subMessage = `${currentUserWinAmount} ETB`;
          modalMessage = `(Shared with ${allWinnersList.length - 1} other${allWinnersList.length - 1 > 1 ? "s" : ""})`;
        } else {
          titleMessage = "🎉 YOU WON! 🎉";
          subMessage = `${currentUserWinAmount} ETB`;
          modalMessage = "";
        }
      } else {
        titleMessage = "😭 GAME OVER 😭";
        if (data.noWinner) {
          modalMessage = t("noBingoMessage");
          subMessage = "";
        } else if (allWinnersList.length > 1) {
          modalMessage = `${allWinnersList.length} winners shared the prize!`;
          subMessage = `Each winner got ${allWinnersList[0]?.winAmount} ETB`;
        } else if (allWinnersList.length === 1) {
          const winner = allWinnersList[0];
          modalMessage = `${winner.fullName} won ${winner.winAmount} ETB!`;
          subMessage = "";
        } else {
          modalMessage = "Game ended without a winner.";
          subMessage = "";
        }
      }

      // For single winner display
      let winnerName = "";
      let maskedPhone = null;
      let winnerWinAmount = "0";

      if (!isCurrentUserWinner && allWinnersList.length === 1) {
        const winner = allWinnersList[0];
        winnerName = winner.fullName || t("unknownPlayer");
        winnerWinAmount = winner.winAmount;
        maskedPhone = winner.phoneNumber
          ? `${winner.phoneNumber.substring(0, 3)}***${winner.phoneNumber.substring(winner.phoneNumber.length - 2)}`
          : null;
      }

      setWinnerInfo({
        winnerId: allWinnersList[0]?.userId || null,
        winAmount: currentUserWinAmount,
        winnerWinAmount: winnerWinAmount,
        message: modalMessage,
        titleMessage: titleMessage,
        subMessage: subMessage,
        ownerFullName: winnerName,
        ownerPhoneNumber: maskedPhone,
        noWinner: data.noWinner || false,
        isMultiWinner: allWinnersList.length > 1,
        winningCartellaBoard: null,
        winningPatternCells: [],
        winningCartellaAllMarkedNumbers: [],
        winningCartellaId: null,
        allWinners: allWinnersList,
        allWinningBoards: allWinningBoards,
      });

      setShowGameEndModal(true);

      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    },
    [cleanupAudio, t, gameId],
  );

  const fetchInitialData = useCallback(async () => {
    setLoadingGameData(true);
    shouldStopSocketRef.current = false;

    setWinnerInfo({
      message: "",
      noWinner: false,
      isMultiWinner: false,
      winnerId: null,
      winAmount: "0",
      ownerFullName: "",
      ownerPhoneNumber: null,
      winningCartellaBoard: null,
      winningPatternCells: [],
      winningCartellaAllMarkedNumbers: [],
      winningCartellaId: null,
      allWinners: [],
      titleMessage: "",
      subMessage: "",
    });
    setShowGameEndModal(false);

    if (!gameId) {
      setTimeout(() => cleanupAndNavigate("/stake-selection"), 2000);
      setLoadingGameData(false);
      return;
    }

    const userId = localStorage.getItem("userId");
    if (!userId) {
      cleanupAndNavigate("/login");
      return;
    }

    try {
      const userCartellasResponse = await axiosInstance.get(
        `/game/${gameId}/user-cartellas`,
      );
      const fetchedUserCartellas = userCartellasResponse.data.cartellas;

      if (fetchedUserCartellas.length === 0) {
        setTimeout(() => cleanupAndNavigate("/stake-selection"), 2000);
        setLoadingGameData(false);
        return;
      }
      setUserCartellas(fetchedUserCartellas);

      const gameStateResponse = await axiosInstance.get(
        `/game/${gameId}/state`,
      );
      const game = gameStateResponse.data.game;

      await loadMarkedNumbers();

      const stake = parseFloat(game.stake_amount) || 0;
      const totalCartellas = game.total_cartellas || 0;

      setGameStake(stake);
      setCardCount(totalCartellas);
      setGameStatus(game.status);
      setNextDrawTime(game.next_draw_time);
      setPlayerCount(game.total_players);

      if (game.calledBalls && Array.isArray(game.calledBalls)) {
        setCalledBalls(game.calledBalls);
        if (game.currentBall) {
          setCurrentBall(game.currentBall);
          setPreviousCalls(
            game.calledBalls.filter((num) => num !== game.currentBall),
          );
        } else if (game.calledBalls.length > 0) {
          setCurrentBall(game.calledBalls[game.calledBalls.length - 1]);
          setPreviousCalls(game.calledBalls.slice(0, -1));
        }
      }

      if (
        game.status === "completed" ||
        game.status === "failed_start" ||
        game.status === "no_winner" ||
        game.status === "bingo_claimed_multi_winner"
      ) {
        gameEnded.current = true;
        shouldStopSocketRef.current = true;

        const gameEndData = {
          winners: game.winners || [],
          noWinner: !game.winners || game.winners.length === 0,
          isMultiWinner: game.is_multi_winner || false,
          endStatus: game.status,
          status: game.status,
          winAmount: game.winners?.[0]?.winAmount,
          winningCartellaBoard: game.winning_cartella_board,
          winningPatternCells: game.winning_pattern_cells,
          winningCartellaId: game.winning_cartella_id,
        };

        handleGameEndLogic(gameEndData);
        setLoadingGameData(false);
        return;
      }
    } catch (err) {
      console.error("Error fetching initial data:", err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("token");
        cleanupAndNavigate("/login");
      }
    } finally {
      setLoadingGameData(false);
    }
  }, [gameId, cleanupAndNavigate, t, loadMarkedNumbers, handleGameEndLogic]);

  useEffect(() => {
    if (
      !shouldStopSocketRef.current &&
      isAutoMarkEnabled &&
      currentBall &&
      typeof currentBall === "number" &&
      gameStatus === "active"
    ) {
      autoMarkNumber(currentBall);
      playNumberSound(currentBall, language);
      setFlipBall(true);
      const timer = setTimeout(() => {
        setFlipBall(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [
    currentBall,
    autoMarkNumber,
    playNumberSound,
    language,
    gameStatus,
    isAutoMarkEnabled,
  ]);

  useEffect(() => {
    if (gameId) {
      fetchInitialData();
    }
  }, [gameId, fetchInitialData]);

  useEffect(() => {
    isMounted.current = true;
    gameEnded.current = false;
    shouldStopSocketRef.current = false;

    loadAllAudio(language);

    return () => {
      isMounted.current = false;
      shouldStopSocketRef.current = true;
      cleanupAudio();
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [loadAllAudio, cleanupAudio, language]);

  useEffect(() => {
    if (
      isMuted ||
      !winnerInfo.message ||
      (winnerInfo.winAmount === "0" && !winnerInfo.noWinner)
    ) {
      return;
    }

    const currentUserId = localStorage.getItem("userId");
    const isWinner = winnerInfo.allWinners?.some(
      (w) => String(w.userId) === String(currentUserId),
    );

    if (isWinner) {
      playWinSound(language);
    } else if (winnerInfo.message && !winnerInfo.noWinner) {
      playLoseSound(language);
    }
  }, [winnerInfo, language, isMuted, playWinSound, playLoseSound]);

  useEffect(() => {
    const currentUserId = localStorage.getItem("userId");
    const isWinner = winnerInfo.allWinners?.some(
      (w) => String(w.userId) === String(currentUserId),
    );

    if (!isWinner) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      return;
    }

    const animate = () => {
      setConfettiPieces((prev) => {
        const updated = prev
          .map((p) => ({
            ...p,
            y: p.y + p.speed,
            rotation: p.rotation + p.rotationSpeed,
            opacity: p.y > 90 ? 0 : 1,
          }))
          .filter((p) => p.y < 100 && p.opacity > 0);

        if (Math.random() > 0.7) {
          return [...updated, ...createConfetti(5)];
        }
        return updated;
      });

      setFireworks((prev) => {
        const updated = prev
          .map((f) => ({
            ...f,
            x: f.x + f.vx,
            y: f.y + f.vy,
            vy: f.vy + 0.05,
            life: f.life - 1,
            size: f.size * 0.99,
          }))
          .filter((f) => f.life > 0);

        if (Math.random() > 0.9) {
          const x = Math.random() * 80 + 10;
          const y = Math.random() * 30;
          return [...updated, ...createFirework(x, y)];
        }
        return updated;
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    setConfettiPieces(createConfetti(200));
    setFireworks(createFirework(50, 30));
    setFireworks((prev) => [...prev, ...createFirework(30, 20)]);
    setFireworks((prev) => [...prev, ...createFirework(70, 25)]);

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [winnerInfo, createConfetti, createFirework]);

  useEffect(() => {
    if (gameStake > 0 && cardCount > 0) {
      const calculatedWinPotential = gameStake * cardCount * 0.75;
      setWinPotential(calculatedWinPotential);
    } else {
      setWinPotential(0);
    }
  }, [gameStake, cardCount]);

  useEffect(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    if (
      gameStatus === "pending" &&
      nextDrawTime &&
      !shouldStopSocketRef.current
    ) {
      const updateCountdown = () => {
        const currentCount = calculateCountdown(nextDrawTime);
        setCountdown(formatTime(currentCount));
        if (currentCount <= 0) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
          fetchInitialData();
        }
      };

      updateCountdown();
      countdownIntervalRef.current = setInterval(updateCountdown, 1000);
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [
    gameStatus,
    nextDrawTime,
    calculateCountdown,
    formatTime,
    fetchInitialData,
  ]);

  useEffect(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    if (
      (gameStatus === "active" || gameStatus === "pending") &&
      !showGameEndModal &&
      gameId &&
      !shouldStopSocketRef.current
    ) {
      pollingIntervalRef.current = setInterval(async () => {
        try {
          const response = await axiosInstance.get(`/game/${gameId}/state`);
          const game = response.data.game;

          if (
            game.status === "completed" ||
            game.status === "failed_start" ||
            game.status === "no_winner" ||
            game.status === "bingo_claimed_multi_winner"
          ) {
            console.log("Polling detected game end:", game.status);

            const gameEndData = {
              winners: game.winners || [],
              noWinner: !game.winners || game.winners.length === 0,
              isMultiWinner: game.is_multi_winner || false,
              endStatus: game.status,
              status: game.status,
              winAmount: game.winners?.[0]?.winAmount,
              winningCartellaBoard: game.winning_cartella_board,
              winningPatternCells: game.winning_pattern_cells,
              winningCartellaId: game.winning_cartella_id,
            };

            handleGameEndLogic(gameEndData);
          }
        } catch (error) {
          console.error("Polling error:", error);
          if (
            error.response?.status === 401 ||
            error.response?.status === 403
          ) {
            localStorage.removeItem("token");
            cleanupAndNavigate("/login");
          }
        }
      }, 5000);
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [
    gameStatus,
    showGameEndModal,
    gameId,
    handleGameEndLogic,
    cleanupAndNavigate,
  ]);

  const socketEventHandlers = useMemo(
    () => ({
      connectionStatus: (isConnected) => {},
      error: (message) => {
        if (message.includes("Authentication error")) {
          localStorage.removeItem("token");
          cleanupAndNavigate("/login");
        }
      },
      gameUpdate: (data) => {
        if (shouldStopSocketRef.current) return;
        if (data.calledBalls) {
          setCalledBalls([...data.calledBalls]);
          if (data.currentBall !== undefined) {
            setCurrentBall(data.currentBall);
            setPreviousCalls(
              data.calledBalls.filter((num) => num !== data.currentBall),
            );
          }
        }
        if (data.playerCount !== undefined) setPlayerCount(data.playerCount);
        if (data.cardCount !== undefined) setCardCount(data.cardCount);
        if (data.gameStake !== undefined)
          setGameStake(parseFloat(data.gameStake));
        if (data.gameStatus && data.gameStatus !== gameStatus)
          setGameStatus(data.gameStatus);
        if (data.nextDrawTime) setNextDrawTime(new Date(data.nextDrawTime));
      },
      gameEnd: (data) => {
        if (!gameEnded.current && !shouldStopSocketRef.current) {
          handleGameEndLogic(data);
        }
      },
    }),
    [gameStatus, handleGameEndLogic, cleanupAndNavigate],
  );

  const { socketConnected, emit, gameData } = useGameSocket(
    shouldStopSocketRef.current ? null : gameId,
    socketEventHandlers,
    cleanupAndNavigate,
  );

  useEffect(() => {
    if (gameData && !shouldStopSocketRef.current) {
      setCalledBalls(gameData.calledBalls ? [...gameData.calledBalls] : []);
      setCurrentBall(
        gameData.currentBall !== undefined ? gameData.currentBall : null,
      );
      setPreviousCalls(
        gameData.calledBalls
          ? gameData.calledBalls.filter((num) => num !== gameData.currentBall)
          : [],
      );
      setPlayerCount(gameData.playerCount || 0);
      setCardCount(gameData.cardCount || 0);
      setGameStake(parseFloat(gameData.gameStake || 0));
      setGameStatus(gameData.gameStatus);
      setNextDrawTime(gameData.nextDrawTime);
    }
  }, [gameData]);

  const getPreparedBoard = useCallback(
    (rawBoard, cartellaId) => {
      if (!rawBoard || rawBoard.length === 0) return null;

      const formattedBoard = {};
      const letters = ["B", "I", "N", "G", "O"];
      let currentRawIndex = 0;

      const tempGridRowMajor = Array(5)
        .fill(null)
        .map(() => Array(5).fill(null));

      for (let rowIdx = 0; rowIdx < 5; rowIdx++) {
        for (let colIdx = 0; colIdx < 5; colIdx++) {
          if (letters[colIdx] === "N" && rowIdx === 2) {
            tempGridRowMajor[rowIdx][colIdx] = "F";
          } else {
            if (currentRawIndex < rawBoard.length) {
              tempGridRowMajor[rowIdx][colIdx] = rawBoard[currentRawIndex];
              currentRawIndex++;
            } else {
              tempGridRowMajor[rowIdx][colIdx] = null;
            }
          }
        }
      }

      const currentCartellaMarkedNumbers = new Set(
        markedNumbersByCartella[cartellaId] || ["F"],
      );

      letters.forEach((letter, colIdx) => {
        const column = [];
        for (let rowIdx = 0; rowIdx < 5; rowIdx++) {
          const num = tempGridRowMajor[rowIdx][colIdx];
          column.push({
            number: num,
            display:
              num === "F" ? t("free") : num !== null ? num.toString() : "",
            called: calledBalls.includes(num),
            markedByUser: currentCartellaMarkedNumbers.has(num),
          });
        }
        formattedBoard[letter] = column;
      });

      return formattedBoard;
    },
    [calledBalls, markedNumbersByCartella, t],
  );

  const getPreparedWinningBoard = useMemo(() => {
    const rawBoard = winnerInfo.winningCartellaBoard;
    if (!rawBoard || rawBoard.length === 0) return null;

    const formattedBoard = {};
    const letters = ["B", "I", "N", "G", "O"];
    const allCalledBalls = calledBalls;
    const winnerAllMarkedNumbers = new Set(
      winnerInfo.winningCartellaAllMarkedNumbers || [],
    );
    const winningCellsList = winnerInfo.winningPatternCells || [];

    const tempGridRowMajor = Array(5)
      .fill(null)
      .map(() => Array(5).fill(null));

    if (rawBoard[0] && Array.isArray(rawBoard[0]) && rawBoard[0].length === 5) {
      for (let c = 0; c < 5; c++) {
        for (let r = 0; r < 5; r++) {
          if (rawBoard[c] && rawBoard[c][r] !== undefined) {
            tempGridRowMajor[r][c] = rawBoard[c][r];
          }
        }
      }
    } else {
      let idx = 0;
      for (let r = 0; r < 5; r++) {
        for (let c = 0; c < 5; c++) {
          if (idx < rawBoard.length) {
            tempGridRowMajor[r][c] = rawBoard[idx++];
          }
        }
      }
    }

    letters.forEach((letter, colIdx) => {
      const column = [];
      for (let rowIdx = 0; rowIdx < 5; rowIdx++) {
        const num = tempGridRowMajor[rowIdx][colIdx];
        column.push({
          number: num,
          display: num === "F" ? t("free") : num !== null ? num.toString() : "",
          called: allCalledBalls.includes(num),
          markedByUser: winnerAllMarkedNumbers.has(num),
        });
      }
      formattedBoard[letter] = column;
    });

    return { board: formattedBoard, winningCells: winningCellsList };
  }, [
    winnerInfo.winningCartellaBoard,
    calledBalls,
    winnerInfo.winningCartellaAllMarkedNumbers,
    winnerInfo.winningPatternCells,
    t,
  ]);

  if (
    loadingGameData ||
    !gameId ||
    userCartellas.length === 0 ||
    gameStatus === null
  ) {
    return (
      <PageWrapper
        {...props}
        backgroundClass="bg-blue-900 text-white min-h-screen flex items-center justify-center"
      >
        <p className="text-xl">{t("loadingGame")}</p>
        <ToastContainer />
      </PageWrapper>
    );
  }

  const currentUserId = localStorage.getItem("userId");
  const isCurrentUserWinner = winnerInfo.allWinners?.some(
    (w) => String(w.userId) === String(currentUserId),
  );

  return (
    <PageWrapper {...props}>
      <ToastContainer />
      {isAudioPlaying && !isMuted && (
        <div className="fixed top-4 right-4 bg-blue-500 text-white px-3 py-1 rounded-full text-xs flex items-center z-50">
          <span className="animate-pulse mr-2">🔊</span>
          {t("playing")}
        </div>
      )}

      {isAutoMarking && (
        <div className="fixed bottom-4 left-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs flex items-center z-50 animate-pulse">
          <span className="mr-1">✨</span>
          {t("autoMarking")}
        </div>
      )}

      <div className="relative flex-grow flex flex-col overflow-y-auto pt-4">
        <SharedHeader
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />
        <Sidebar
          isOpen={isSidebarOpen}
          toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        />

        <div className="grid grid-cols-6 gap-1 p-2 sm:p-3 text-center text-[0.65rem] sm:text-xs md:text-sm bg-gray-900 bg-opacity-70 mt-1">
          <div className="flex flex-col items-center justify-center h-9 sm:h-11">
            <span className="font-bold text-yellow-300 text-[0.6rem] sm:text-xs">
              {t("winPotential")}
            </span>
            <span className="font-digital text-sm sm:text-base text-white">
              {winPotential.toFixed(2)}
            </span>
          </div>
          <div className="flex flex-col items-center justify-center h-9 sm:h-11">
            <span className="font-bold text-yellow-300 text-[0.6rem] sm:text-xs">
              {t("stake")}
            </span>
            <span className="font-digital text-sm sm:text-base text-white">
              {gameStake.toFixed(2)}
            </span>
          </div>
          <div className="flex flex-col items-center justify-center h-9 sm:h-11">
            <span className="font-bold text-yellow-300 text-[0.6rem] sm:text-xs">
              {t("called")}
            </span>
            <span className="font-digital text-sm sm:text-base text-white">
              {calledBalls.length}
            </span>
          </div>
          <div className="flex flex-col items-center justify-center h-9 sm:h-11">
            <span className="font-bold text-yellow-300 text-[0.6rem] sm:text-xs">
              {t("player")}
            </span>
            <span className="font-digital text-sm sm:text-base text-white">
              {playerCount}
            </span>
          </div>

          <div className="flex flex-col items-center justify-center h-9 sm:h-11">
            <span className="font-bold text-yellow-300 text-[0.6rem] sm:text-xs">
              {isAutoMarkEnabled ? t("autoMode") : t("manualMode")}
            </span>
            <label
              htmlFor="auto-mark-toggle"
              className="flex items-center cursor-pointer mt-0.5"
            >
              <div className="relative">
                <input
                  type="checkbox"
                  id="auto-mark-toggle"
                  className="sr-only"
                  checked={isAutoMarkEnabled}
                  onChange={() => setIsAutoMarkEnabled(!isAutoMarkEnabled)}
                />
                <div
                  className={`block w-10 h-5 rounded-full ${isAutoMarkEnabled ? "bg-green-600" : "bg-gray-500"} transition-colors duration-300 ease-in-out`}
                ></div>
                <div
                  className={`dot absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform duration-300 ease-in-out ${isAutoMarkEnabled ? "translate-x-5" : "translate-x-0"}`}
                ></div>
              </div>
            </label>
          </div>

          <div className="flex flex-col items-center justify-center h-9 sm:h-11">
            <span className="font-bold text-yellow-300 text-[0.6rem] sm:text-xs">
              {isMuted ? t("unmute") : t("mute")}
            </span>
            <label
              htmlFor="audio-toggle"
              className="flex items-center cursor-pointer mt-0.5"
            >
              <div className="relative">
                <input
                  type="checkbox"
                  id="audio-toggle"
                  className="sr-only"
                  checked={!isMuted}
                  onChange={toggleMute}
                />
                <div
                  className={`block w-10 h-5 rounded-full ${!isMuted ? "bg-blue-600" : "bg-gray-500"} transition-colors duration-300 ease-in-out`}
                ></div>
                <div
                  className={`dot absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform duration-300 ease-in-out ${!isMuted ? "translate-x-5" : "translate-x-0"}`}
                ></div>
              </div>
            </label>
          </div>
        </div>

        <div className="flex flex-row flex-grow w-full max-w-full mx-auto p-2 gap-1 min-w-0">
          <div className="w-1/2 flex-col bg-purple-800 bg-opacity-30 rounded-lg p-2 shadow-lg border border-purple-700 overflow-auto flex-shrink-0">
            <div className="lg:hidden w-full flex justify-center mb-2">
              <MiniBingoBoard
                calledNumbers={calledBalls}
                getBallColor={getBallColor}
              />
            </div>
            <div className="hidden lg:block w-full">
              <FullBingoBoard
                calledNumbers={calledBalls}
                getBallColor={getBallColor}
              />
            </div>
          </div>

          <div className="w-1/2 bg-purple-800 bg-opacity-30 rounded-lg p-2 shadow-lg flex flex-col items-center justify-start border border-purple-700 min-w-0 flex-grow">
            <div className="flex flex-col items-center mb-2 w-full">
              {currentBall ? (
                <BingoBall
                  key={currentBall}
                  number={currentBall}
                  size="lg"
                  flip={flipBall}
                />
              ) : (
                <div className="rounded-full w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center bg-gray-700 border-2 border-gray-600">
                  <span className="text-sm sm:text-base text-gray-400">
                    {t("waitingBallCall")}
                  </span>
                </div>
              )}
            </div>

            {userCartellas.length > 0 && (
              <div className="w-full flex flex-col items-center mt-1 px-1">
                <div className="grid grid-cols-1 gap-1 w-full max-w-full">
                  {userCartellas.map((cartella) => (
                    <div key={cartella.cartella_id}>
                      <p className="text-sm font-semibold mb-1 text-yellow-300 text-center">
                        {t("cardNumber", { number: cartella.cartella_id })}
                      </p>
                      <BingoBoard
                        board={getPreparedBoard(
                          cartella.numbers,
                          cartella.cartella_id,
                        )}
                        playerMarkedNumbers={
                          new Set(
                            markedNumbersByCartella[cartella.cartella_id] || [
                              "F",
                            ],
                          )
                        }
                        isWinningBoardDisplay={false}
                        onCellClick={handleCellClick}
                        isInteractive={
                          gameStatus === "active" &&
                          !shouldStopSocketRef.current
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Game End Modal - NO DUPLICATION */}
      {showGameEndModal && (
        <div
          ref={containerRef}
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 overflow-hidden p-1 sm:p-2"
        >
          {/* Confetti and fireworks only for winners */}
          {isCurrentUserWinner && (
            <>
              {confettiPieces.map((piece) => (
                <div
                  key={piece.id}
                  style={{
                    position: "absolute",
                    left: `${piece.x}%`,
                    top: `${piece.y}%`,
                    width: `${piece.size}px`,
                    height: `${piece.size}px`,
                    backgroundColor: piece.color,
                    borderRadius: piece.shape === "circle" ? "50%" : "0",
                    transform: `rotate(${piece.rotation}deg)`,
                    opacity: piece.opacity,
                    pointerEvents: "none",
                    zIndex: 10,
                  }}
                />
              ))}
              {fireworks.map((firework) => (
                <div
                  key={firework.id}
                  style={{
                    position: "absolute",
                    left: `${firework.x}%`,
                    top: `${firework.y}%`,
                    width: `${firework.size}px`,
                    height: `${firework.size}px`,
                    backgroundColor: firework.color,
                    borderRadius: "50%",
                    opacity: firework.life / 100,
                    pointerEvents: "none",
                    zIndex: 10,
                    boxShadow: `0 0 ${firework.size}px ${firework.size / 2}px ${firework.color}`,
                  }}
                />
              ))}
            </>
          )}

          <div
            className="text-center bg-gray-900 bg-opacity-90 p-4 sm:p-6 rounded-2xl shadow-2xl w-full max-w-xl mx-auto relative z-20 transform transition-all duration-500 overflow-y-auto max-h-[95vh]"
            style={{
              background:
                "linear-gradient(135deg, rgba(17,24,39,0.95), rgba(31,41,55,0.95))",
              boxShadow: "0 0 40px rgba(255, 215, 0, 0.6)",
            }}
          >
            {isCurrentUserWinner ? (
              // WINNER MODAL
              <>
                <div className="animate-bounce mb-5">
                  <h3 className="text-3xl md:text-4xl font-extrabold text-yellow-400">
                    {winnerInfo.titleMessage}
                  </h3>
                </div>

                <div className="animate-pulse mb-6">
                  <p className="text-4xl md:text-5xl text-white font-digital mb-2">
                    <span className="text-green-400 font-extrabold">
                      {winnerInfo.subMessage}
                    </span>
                  </p>
                  {winnerInfo.isMultiWinner && (
                    <p className="text-md text-yellow-200">
                      {winnerInfo.message}
                    </p>
                  )}
                  <p className="text-lg text-yellow-300 mt-3">
                    is coming your way! ✨
                  </p>
                </div>

                <div className="flex justify-center mb-6">
                  <div className="w-24 h-24 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center animate-spin-slow shadow-xl">
                    <span className="text-5xl">💰</span>
                  </div>
                </div>

                <p className="text-md text-blue-200 mb-4 animate-fade-in">
                  Congratulations! Your winnings will be credited shortly.
                </p>
              </>
            ) : (
              // NON-WINNER MODAL - ONLY SHOW WINNING CARTELLAS, NOT DUPLICATE WINNER LIST
              <>
                <h3 className="text-3xl md:text-4xl font-extrabold text-red-400 mb-4">
                  {winnerInfo.titleMessage}
                </h3>

                {winnerInfo.noWinner ? (
                  <div className="bg-red-900 bg-opacity-50 p-4 rounded-xl mb-4 animate-pulse">
                    <p className="text-lg text-white">{winnerInfo.message}</p>
                  </div>
                ) : (
                  <>
                    {/* Show game result message without duplicate winner list */}
                    <div className="bg-blue-900 bg-opacity-50 p-4 rounded-xl mb-4">
                      <p className="text-xl text-white font-semibold text-center">
                        {winnerInfo.message}
                      </p>
                      {winnerInfo.subMessage && (
                        <p className="text-md text-yellow-200 text-center mt-2">
                          {winnerInfo.subMessage}
                        </p>
                      )}
                    </div>

                    {/* Show ALL Winners' Cartella Boards with marked and winning cells */}
                    {winnerInfo.allWinningBoards &&
                      winnerInfo.allWinningBoards.length > 0 && (
                        <div className="mt-4">
                          <h3 className="text-md font-bold text-yellow-300 mb-2 text-center">
                            🎯 Winning Cartellas (
                            {winnerInfo.allWinningBoards.length})
                          </h3>
                          <div className="flex flex-wrap justify-center gap-3 max-h-[400px] overflow-y-auto p-2">
                            {winnerInfo.allWinningBoards.map(
                              (winnerBoard, idx) => {
                                const reconstructedBoard =
                                  reconstructBoardFrom1D(winnerBoard.boardData);
                                if (!reconstructedBoard) return null;

                                // Convert winning cells to numbers (they might be strings)
                                const winningCellsNumbers = (
                                  winnerBoard.winningCells || []
                                )
                                  .map((c) =>
                                    typeof c === "string" ? parseInt(c, 10) : c,
                                  )
                                  .filter((c) => !isNaN(c) && c !== "F");

                                return (
                                  <div
                                    key={idx}
                                    className="flex flex-col items-center bg-gray-800 rounded-lg p-2"
                                  >
                                    <p className="text-xs font-semibold text-yellow-300 mb-1">
                                      {winnerBoard.fullName}
                                    </p>
                                    <p className="text-xs text-blue-300 mb-1">
                                      Card No. {winnerBoard.cartellaId} • Won{" "}
                                      {winnerBoard.winAmount} ETB
                                    </p>
                                    <div className="w-48 sm:w-52">
                                      <BingoBoard
                                        board={reconstructedBoard}
                                        winningCells={winningCellsNumbers}
                                        playerMarkedNumbers={
                                          winnerBoard.markedNumbers ||
                                          new Set(["F"])
                                        }
                                        isWinningBoardDisplay={true}
                                        isInteractive={false}
                                      />
                                    </div>
                                  </div>
                                );
                              },
                            )}
                          </div>
                        </div>
                      )}
                  </>
                )}

                <div className="text-sm text-yellow-200 mt-4">
                  Better luck next time!
                </div>
              </>
            )}

            <button
              onClick={() => cleanupAndNavigate("/stake-selection")}
              className="mt-6 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-3 px-8 rounded-full text-base shadow-lg transform hover:scale-105 transition-transform duration-200"
            >
              Play Again!
            </button>
          </div>
        </div>
      )}

      {gameStatus !== "active" && !showGameEndModal && (
        <div className="fixed inset-0 bg-blue-900 text-white flex items-center justify-center z-40">
          <p className="text-xl">{t("waitingPlayers")}</p>
        </div>
      )}

      <style>{`
        @keyframes float-1 { 0%, 100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-20px) rotate(10deg); } }
        @keyframes float-2 { 0%, 100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-30px) rotate(-5deg); } }
        @keyframes float-3 { 0%, 100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-15px) rotate(5deg); } }
        @keyframes float-4 { 0%, 100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-25px) rotate(-10deg); } }
        @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes bounce-slow { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-float-1 { animation: float-1 6s ease-in-out infinite; }
        .animate-float-2 { animation: float-2 8s ease-in-out infinite; }
        .animate-float-3 { animation: float-3 7s ease-in-out infinite; }
        .animate-float-4 { animation: float-4 5s ease-in-out infinite; }
        .animate-spin-slow { animation: spin-slow 10s linear infinite; }
        .animate-bounce-slow { animation: bounce-slow 2s ease-in-out infinite; }
        .animate-fade-in { animation: fade-in 1s ease-out; }
      `}</style>
    </PageWrapper>
  );
};

export default PlayCartellaPage;
